<template>
  <div class="admin-page">
    <!-- 侧边栏 -->
    <aside class="sidebar">
      <div class="logo">
        <span class="logo-icon">📦</span>
        <span class="logo-text">订单管理系统</span>
      </div>
      <nav class="sidebar-nav">
        <a href="#" class="nav-item" :class="{ active: currentView === 'orders' }" @click.prevent="currentView = 'orders'">
          <span class="nav-icon">📦</span>
          <span class="nav-label">订单管理</span>
        </a>
        <a href="#" class="nav-item" :class="{ active: currentView === 'users' }" @click.prevent="currentView = 'users'">
          <span class="nav-icon">👥</span>
          <span class="nav-label">用户管理</span>
        </a>
        <a href="#" class="nav-item" @click.prevent="showPasswordModal = true">
          <span class="nav-icon">🔑</span>
          <span class="nav-label">修改密码</span>
        </a>
        <a href="#" class="nav-item" @click.prevent="logout">
          <span class="nav-icon">🚪</span>
          <span>退出登录</span>
        </a>
      </nav>
    </aside>

    <!-- 主内容区 -->
    <main class="main-content">
      <!-- 顶部栏 -->
      <header class="top-bar">
        <h1 class="page-title">订单管理</h1>
        <div class="user-info" v-if="user">
          <span class="avatar">{{ user.username ? user.username[0].toUpperCase() : 'U' }}</span>
          <span class="username">{{ user.username }}</span>
        </div>
      </header>

      <!-- 内容区 -->
      <div class="content-area">
        <!-- 订单列表 -->
        <div class="card table-card" v-if="currentView === 'orders'">
          <div class="card-header">
            <h3>订单列表</h3>
            <div class="header-actions">
              <input 
                v-model="searchKeyword" 
                @keyup.enter="searchOrders"
                placeholder="搜索客户参考号/运单号/转单号" 
                class="search-input"
              />
              <button class="btn-search" @click="searchOrders">搜索</button>
              <button class="btn-primary" @click="openCreateModal">+ 新增订单</button>
              <button class="btn-icon" @click="loadOrders" :disabled="loadingList" title="刷新">
                <span :class="{ spinning: loadingList }">🔄</span>
              </button>
            </div>
          </div>
          <div class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>客户参考号</th>
                  <th>运单号</th>
                  <th>转单号</th>
                  <th>目的地</th>
                  <th>件数</th>
                  <th>邮编</th>
                  <th>创建时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="orders.length === 0">
                  <td colspan="9" class="empty-row">暂无订单数据</td>
                </tr>
                <tr v-for="o in orders" :key="o.id">
                  <td>{{ o.id }}</td>
                  <td>{{ o.customer_ref }}</td>
                  <td class="link-cell">{{ o.waybill_no }}</td>
                  <td>{{ o.transfer_no || '-' }}</td>
                  <td>{{ o.destination }}</td>
                  <td>{{ o.total_pieces }}</td>
                  <td>{{ o.recipient_zip || '-' }}</td>
                  <td>{{ formatDate(o.created_at) }}</td>
                  <td class="action-cell" @click.stop>
                    <button class="btn-sm btn-edit" @click="openDetailModal(o)" title="编辑">编辑</button>
                    <button class="btn-sm btn-delete" @click="deleteOrder(o.id)" title="删除">删除</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- 分页 -->
          <div class="pagination" v-if="totalPages > 1">
            <button class="page-btn" :disabled="page <= 1" @click="goPage(page - 1)">上一页</button>
            <span class="page-info">第 {{ page }} / {{ totalPages }} 页，共 {{ total }} 条</span>
            <button class="page-btn" :disabled="page >= totalPages" @click="goPage(page + 1)">下一页</button>
          </div>
        </div>

        <!-- 用户管理 -->
        <div class="card table-card" v-if="currentView === 'users'">
          <div class="card-header">
            <h3>用户管理</h3>
            <div class="header-actions">
              <button class="btn-primary" @click="openCreateUserModal">+ 新增用户</button>
            </div>
          </div>
          <div class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>用户名</th>
                  <th>创建时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="users.length === 0">
                  <td colspan="4" class="empty-row">暂无用户数据</td>
                </tr>
                <tr v-for="u in users" :key="u.id">
                  <td>{{ u.id }}</td>
                  <td>{{ u.username }}</td>
                  <td>{{ formatDate(u.created_at) }}</td>
                  <td class="action-cell" @click.stop>
                    <button class="btn-sm btn-delete" @click="deleteUser(u.id)" title="删除">删除</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>

    <!-- 新增订单弹窗 -->
    <div class="modal-overlay" v-if="showCreateModal" @click.self="closeCreateModal">
      <div class="modal">
        <div class="modal-header">
          <h3>新增订单</h3>
          <button class="modal-close" @click="closeCreateModal">×</button>
        </div>
        <form class="modal-body" @submit.prevent="createOrder">
          <div class="form-row">
            <div class="form-group">
              <label>客户参考号 <span class="required">*</span></label>
              <input v-model.trim="form.customer_ref" placeholder="请输入客户参考号" required />
            </div>
            <div class="form-group">
              <label>运单号 <span class="required">*</span></label>
              <input v-model.trim="form.waybill_no" placeholder="请输入运单号" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>转单号</label>
              <input v-model.trim="form.transfer_no" placeholder="请输入转单号（可选）" />
            </div>
            <div class="form-group">
              <label>总件数 <span class="required">*</span></label>
              <input v-model.number="form.total_pieces" type="number" min="1" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>目的地 <span class="required">*</span></label>
              <input v-model.trim="form.destination" placeholder="请输入目的地" required />
            </div>
            <div class="form-group">
              <label>收件人邮编</label>
              <input v-model.trim="form.recipient_zip" placeholder="请输入邮编（可选）" />
            </div>
          </div>
          <p v-if="errorCreate" class="error-msg">{{ errorCreate }}</p>
          <div class="modal-footer">
            <button type="button" class="btn-secondary" @click="closeCreateModal">取消</button>
            <button type="submit" class="btn-primary" :disabled="loadingCreate">
              {{ loadingCreate ? '提交中...' : '创建订单' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 订单详情/编辑弹窗 -->
    <div class="modal-overlay" v-if="showDetailModal" @click.self="closeDetailModal">
      <div class="modal modal-large">
        <div class="modal-header">
          <h3>订单详情 #{{ currentOrder.id }}</h3>
          <button class="modal-close" @click="closeDetailModal">×</button>
        </div>
        <div class="modal-body">
          <!-- Tab 切换 -->
          <div class="tabs">
            <button 
              class="tab" 
              :class="{ active: activeTab === 'info' }" 
              @click="activeTab = 'info'"
            >订单信息</button>
            <button 
              class="tab" 
              :class="{ active: activeTab === 'records' }" 
              @click="activeTab = 'records'"
            >物流节点 ({{ currentOrder.records ? currentOrder.records.length : 0 }})</button>
          </div>

          <!-- 订单信息 Tab -->
          <div v-if="activeTab === 'info'" class="tab-content">
            <form @submit.prevent="updateOrder">
              <div class="form-row">
                <div class="form-group">
                  <label>客户参考号 <span class="required">*</span></label>
                  <input v-model.trim="editForm.customer_ref" required />
                </div>
                <div class="form-group">
                  <label>运单号 <span class="required">*</span></label>
                  <input v-model.trim="editForm.waybill_no" required />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>转单号</label>
                  <input v-model.trim="editForm.transfer_no" />
                </div>
                <div class="form-group">
                  <label>总件数 <span class="required">*</span></label>
                  <input v-model.number="editForm.total_pieces" type="number" min="1" required />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>目的地 <span class="required">*</span></label>
                  <input v-model.trim="editForm.destination" required />
                </div>
                <div class="form-group">
                  <label>收件人邮编</label>
                  <input v-model.trim="editForm.recipient_zip" />
                </div>
              </div>

              <!-- 状态时间 -->
              <h4 class="section-title">状态时间</h4>
              <div class="form-row">
                <div class="form-group">
                  <label>入库时间</label>
                  <input type="datetime-local" v-model="editForm.inbound_time" />
                </div>
                <div class="form-group">
                  <label>出库时间</label>
                  <input type="datetime-local" v-model="editForm.outbound_time" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>起运地时间</label>
                  <input type="datetime-local" v-model="editForm.origin_time" />
                </div>
                <div class="form-group">
                  <label>目的地时间</label>
                  <input type="datetime-local" v-model="editForm.destination_time" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>派送时间</label>
                  <input type="datetime-local" v-model="editForm.delivery_time" />
                </div>
                <div class="form-group">
                  <label>签收时间</label>
                  <input type="datetime-local" v-model="editForm.signed_time" />
                </div>
              </div>

              <p v-if="errorUpdate" class="error-msg">{{ errorUpdate }}</p>
              <div class="form-actions">
                <button type="submit" class="btn-primary" :disabled="loadingUpdate">
                  {{ loadingUpdate ? '保存中...' : '保存修改' }}
                </button>
              </div>
            </form>
          </div>

          <!-- 物流节点 Tab -->
          <div v-if="activeTab === 'records'" class="tab-content">
            <!-- 添加节点表单 -->
            <div class="add-record-section">
              <h4>添加物流描述</h4>
              <form class="inline-form" @submit.prevent="addRecord">
                <div class="form-group flex-grow">
                  <label>描述 <span class="required">*</span></label>
                  <input v-model.trim="recordForm.description" placeholder="请输入物流描述信息" required />
                </div>
                <div class="form-group">
                  <label>时间</label>
                  <input type="datetime-local" v-model="recordForm.event_time" />
                </div>
                <button type="submit" class="btn-primary" :disabled="loadingRecord">
                  {{ loadingRecord ? '添加中...' : '添加' }}
                </button>
              </form>
              <p v-if="errorRecord" class="error-msg">{{ errorRecord }}</p>
            </div>

            <!-- 节点列表 -->
            <div class="records-list">
              <div v-if="!currentOrder.records || currentOrder.records.length === 0" class="empty-records">
                暂无物流描述
              </div>
              <div v-else class="timeline">
                <div 
                  v-for="(rec, idx) in sortedRecords" 
                  :key="rec.id || idx" 
                  class="timeline-item"
                >
                  <div class="timeline-dot"></div>
                  <div class="timeline-content">
                    <div class="timeline-header">
                      <span class="timeline-time">{{ formatDateTime(rec.event_time) }}</span>
                      <div class="record-actions">
                        <button class="btn-xs btn-edit" @click="editRecord(rec)">编辑</button>
                        <button class="btn-xs btn-delete" @click="deleteRecord(rec.id)">删除</button>
                      </div>
                    </div>
                    <div class="timeline-desc" v-if="editingRecordId !== rec.id">{{ rec.description }}</div>
                    <!-- 编辑模式 -->
                    <div v-else class="record-edit-form">
                      <input v-model="editRecordForm.description" placeholder="描述" />
                      <input type="datetime-local" v-model="editRecordForm.event_time" />
                      <button class="btn-xs btn-save" @click="saveRecord">保存</button>
                      <button class="btn-xs btn-cancel" @click="cancelEditRecord">取消</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 修改密码弹窗 -->
    <div class="modal-overlay" v-if="showPasswordModal" @click.self="showPasswordModal = false">
      <div class="modal modal-small">
        <div class="modal-header">
          <h3>修改密码</h3>
          <button class="modal-close" @click="showPasswordModal = false">×</button>
        </div>
        <form class="modal-body" @submit.prevent="changePassword">
          <div class="form-group">
            <label>旧密码 <span class="required">*</span></label>
            <input type="password" v-model.trim="passwordForm.oldPassword" required />
          </div>
          <div class="form-group">
            <label>新密码 <span class="required">*</span></label>
            <input type="password" v-model.trim="passwordForm.newPassword" required />
          </div>
          <div class="form-group">
            <label>确认新密码 <span class="required">*</span></label>
            <input type="password" v-model.trim="passwordForm.confirmPassword" required />
          </div>
          <p v-if="passwordError" class="error-msg">{{ passwordError }}</p>
          <div class="modal-footer">
            <button type="button" class="btn-secondary" @click="showPasswordModal = false">取消</button>
            <button type="submit" class="btn-primary" :disabled="loadingPassword">
              {{ loadingPassword ? '修改中...' : '确认修改' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 新增用户弹窗 -->
    <div class="modal-overlay" v-if="showCreateUserModal" @click.self="showCreateUserModal = false">
      <div class="modal modal-small">
        <div class="modal-header">
          <h3>新增用户</h3>
          <button class="modal-close" @click="showCreateUserModal = false">×</button>
        </div>
        <form class="modal-body" @submit.prevent="createUser">
          <div class="form-group">
            <label>用户名 <span class="required">*</span></label>
            <input v-model.trim="userForm.username" required />
          </div>
          <div class="form-group">
            <label>密码 <span class="required">*</span></label>
            <input type="password" v-model.trim="userForm.password" required />
          </div>
          <p v-if="userError" class="error-msg">{{ userError }}</p>
          <div class="modal-footer">
            <button type="button" class="btn-secondary" @click="showCreateUserModal = false">取消</button>
            <button type="submit" class="btn-primary" :disabled="loadingUser">
              {{ loadingUser ? '创建中...' : '创建用户' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

const STATUSES = ['入库', '出库', '起运地', '目的地', '派送', '签收']

export default {
  name: 'AdminPage',
  data() {
    return {
      user: null,
      orders: [],
      loadingList: false,
      loadingCreate: false,
      loadingUpdate: false,
      loadingRecord: false,
      errorCreate: '',
      errorUpdate: '',
      errorRecord: '',
      statuses: STATUSES,
      // 视图控制
      currentView: 'orders',
      // 分页和搜索
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0,
      searchKeyword: '',
      // 弹窗状态
      showCreateModal: false,
      showDetailModal: false,
      showPasswordModal: false,
      showCreateUserModal: false,
      activeTab: 'info',
      currentOrder: {},
      // 用户管理
      users: [],
      loadingUser: false,
      loadingPassword: false,
      userError: '',
      passwordError: '',
      userForm: {
        username: '',
        password: '',
      },
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      // 节点编辑
      editingRecordId: null,
      editRecordForm: {
        description: '',
        event_time: '',
      },
      // 新建表单
      form: {
        customer_ref: '',
        waybill_no: '',
        transfer_no: '',
        total_pieces: 1,
        destination: '',
        recipient_zip: '',
      },
      // 编辑表单
      editForm: {
        customer_ref: '',
        waybill_no: '',
        transfer_no: '',
        total_pieces: 1,
        destination: '',
        recipient_zip: '',
        inbound_time: '',
        outbound_time: '',
        origin_time: '',
        destination_time: '',
        delivery_time: '',
        signed_time: '',
      },
      // 节点表单
      recordForm: {
        description: '',
        event_time: '',
      },
    }
  },
  computed: {
    sortedRecords() {
      if (!this.currentOrder || !Array.isArray(this.currentOrder.records)) return []
      return [...this.currentOrder.records].sort((a, b) => new Date(b.event_time) - new Date(a.event_time))
    },
  },
  created() {
    const stored = localStorage.getItem('authUser')
    if (stored) {
      this.user = JSON.parse(stored)
      this.loadOrders()
      this.loadUsers()
    } else {
      this.$router.replace({ name: 'login', query: { redirect: '/admin' } })
    }
  },
  watch: {
    currentView(newView) {
      if (newView === 'users') {
        this.loadUsers()
      }
    }
  },
  methods: {
    // 格式化日期
    formatDate(val) {
      if (!val) return '-'
      const d = new Date(val)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    formatDateTime(val) {
      if (!val) return '-'
      const d = new Date(val)
      const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
      return `${date} ${time}`
    },
    // 状态样式
    getStatusClass(status) {
      const map = {
        '入库': 'status-inbound',
        '出库': 'status-outbound',
        '起运地': 'status-origin',
        '目的地': 'status-destination',
        '派送': 'status-delivery',
        '签收': 'status-signed',
      }
      return map[status] || ''
    },
    // 登出
    logout() {
      localStorage.removeItem('authUser')
      this.$router.push('/login')
    },
    // 加载用户列表
    async loadUsers() {
      try {
        const resp = await axios.get('/api/users')
        this.users = resp.data.data || []
      } catch (e) {
        // ignore
      }
    },
    // 打开新增用户弹窗
    openCreateUserModal() {
      this.userForm = { username: '', password: '' }
      this.userError = ''
      this.showCreateUserModal = true
    },
    // 创建用户
    async createUser() {
      this.userError = ''
      this.loadingUser = true
      try {
        await axios.post('/api/register', this.userForm)
        alert('用户创建成功')
        this.showCreateUserModal = false
        this.loadUsers()
      } catch (err) {
        this.userError = err.response?.data?.msg || '创建失败'
      } finally {
        this.loadingUser = false
      }
    },
    // 删除用户
    async deleteUser(id) {
      if (!confirm('确定删除该用户吗？')) return
      try {
        await axios.delete(`/api/users/${id}`)
        alert('删除成功')
        this.loadUsers()
      } catch (err) {
        alert('删除失败')
      }
    },
    // 修改密码
    async changePassword() {
      this.passwordError = ''
      if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.passwordError = '两次输入的新密码不一致'
        return
      }
      if (this.passwordForm.newPassword.length < 6) {
        this.passwordError = '新密码长度不能少于6位'
        return
      }
      this.loadingPassword = true
      try {
        await axios.post('/api/change-password', {
          username: this.user.username,
          old_password: this.passwordForm.oldPassword,
          new_password: this.passwordForm.newPassword,
        })
        alert('密码修改成功，请重新登录')
        this.logout()
      } catch (err) {
        this.passwordError = err.response?.data?.msg || '修改失败'
      } finally {
        this.loadingPassword = false
      }
    },
    // 新建弹窗
    openCreateModal() {
      this.form = {
        customer_ref: '',
        waybill_no: '',
        transfer_no: '',
        total_pieces: 1,
        destination: '',
        recipient_zip: '',
      }
      this.errorCreate = ''
      this.showCreateModal = true
    },
    closeCreateModal() {
      this.showCreateModal = false
    },
    // 格式化时间为 datetime-local 格式
    toLocalDateTime(val) {
      if (!val) return ''
      const d = new Date(val)
      if (isNaN(d.getTime())) return ''
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    // 详情弹窗
    openDetailModal(order) {
      this.currentOrder = { ...order }
      this.editForm = {
        customer_ref: order.customer_ref || '',
        waybill_no: order.waybill_no || '',
        transfer_no: order.transfer_no || '',
        total_pieces: order.total_pieces || 1,
        destination: order.destination || '',
        recipient_zip: order.recipient_zip || '',
        inbound_time: this.toLocalDateTime(order.inbound_time),
        outbound_time: this.toLocalDateTime(order.outbound_time),
        origin_time: this.toLocalDateTime(order.origin_time),
        destination_time: this.toLocalDateTime(order.destination_time),
        delivery_time: this.toLocalDateTime(order.delivery_time),
        signed_time: this.toLocalDateTime(order.signed_time),
      }
      this.recordForm = { description: '', event_time: '' }
      this.errorUpdate = ''
      this.errorRecord = ''
      this.activeTab = 'info'
      this.showDetailModal = true
    },
    closeDetailModal() {
      this.showDetailModal = false
    },
    // 加载订单（分页）
    async loadOrders() {
      this.loadingList = true
      try {
        const params = { page: this.page, page_size: this.pageSize }
        if (this.searchKeyword) {
          params.keyword = this.searchKeyword
        }
        const resp = await axios.get('/api/orders', { params })
        const data = resp.data.data || {}
        this.orders = data.list || []
        this.total = data.total || 0
        this.totalPages = data.total_pages || 0
      } catch (e) {
        // ignore
      } finally {
        this.loadingList = false
      }
    },
    // 搜索订单
    searchOrders() {
      this.page = 1
      this.loadOrders()
    },
    // 翻页
    goPage(p) {
      if (p < 1 || p > this.totalPages) return
      this.page = p
      this.loadOrders()
    },
    // 创建订单
    async createOrder() {
      this.errorCreate = ''
      this.loadingCreate = true
      try {
        const payload = { ...this.form, total_pieces: Number(this.form.total_pieces) || 1 }
        await axios.post('/api/orders', payload)
        this.closeCreateModal()
        this.loadOrders()
      } catch (err) {
        this.errorCreate = (err.response?.data?.message) || '创建失败'
      } finally {
        this.loadingCreate = false
      }
    },
    // 更新订单
    async updateOrder() {
      this.errorUpdate = ''
      this.loadingUpdate = true
      try {
        const payload = {
          customer_ref: this.editForm.customer_ref,
          waybill_no: this.editForm.waybill_no,
          transfer_no: this.editForm.transfer_no,
          total_pieces: Number(this.editForm.total_pieces) || 1,
          destination: this.editForm.destination,
          recipient_zip: this.editForm.recipient_zip,
          inbound_time: this.editForm.inbound_time || null,
          outbound_time: this.editForm.outbound_time || null,
          origin_time: this.editForm.origin_time || null,
          destination_time: this.editForm.destination_time || null,
          delivery_time: this.editForm.delivery_time || null,
          signed_time: this.editForm.signed_time || null,
        }
        await axios.put(`/api/orders/${this.currentOrder.id}`, payload)
        this.loadOrders()
        // 更新当前订单显示
        Object.assign(this.currentOrder, payload)
        alert('保存成功')
      } catch (err) {
        this.errorUpdate = (err.response?.data?.message) || '更新失败'
      } finally {
        this.loadingUpdate = false
      }
    },
    // 删除订单
    async deleteOrder(id) {
      if (!confirm(`确认删除订单 ${id} 吗？`)) return
      try {
        await axios.delete(`/api/orders/${id}`)
        this.loadOrders()
      } catch (err) {
        alert('删除失败')
      }
    },
    // 添加节点
    async addRecord() {
      this.errorRecord = ''
      this.loadingRecord = true
      try {
        await axios.post(`/api/orders/${this.currentOrder.id}/records`, this.recordForm)
        await this.reloadCurrentOrder()
        this.recordForm = { description: '', event_time: '' }
      } catch (err) {
        this.errorRecord = (err.response?.data?.message) || '添加失败'
      } finally {
        this.loadingRecord = false
      }
    },
    // 重新加载当前订单
    async reloadCurrentOrder() {
      const resp = await axios.get(`/api/orders/${this.currentOrder.id}`)
      if (resp.data.data) {
        this.currentOrder = resp.data.data
      }
      this.loadOrders()
    },
    // 编辑节点
    editRecord(rec) {
      this.editingRecordId = rec.id
      this.editRecordForm = {
        description: rec.description || '',
        event_time: this.toLocalDateTime(rec.event_time),
      }
    },
    cancelEditRecord() {
      this.editingRecordId = null
    },
    async saveRecord() {
      try {
        await axios.put(`/api/records/${this.editingRecordId}`, this.editRecordForm)
        this.editingRecordId = null
        await this.reloadCurrentOrder()
      } catch (err) {
        alert('保存失败')
      }
    },
    // 删除节点
    async deleteRecord(id) {
      if (!confirm('确认删除此节点吗？')) return
      try {
        await axios.delete(`/api/records/${id}`)
        await this.reloadCurrentOrder()
      } catch (err) {
        alert('删除失败')
      }
    },
  },
}
</script>

<style scoped>
/* 整体布局 */
.admin-page {
  display: flex;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Microsoft YaHei', sans-serif;
  font-size: 14px;
  color: #333;
  background: #f5f7fa;
}

/* 侧边栏 */
.sidebar {
  width: 220px;
  background: linear-gradient(180deg, #1a1a2e 0%, #16213e 100%);
  color: #fff;
  flex-shrink: 0;
}

.logo {
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.logo-icon {
  font-size: 24px;
}

.logo-text {
  font-size: 16px;
  font-weight: 600;
}

.nav-menu {
  padding: 16px 0;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  transition: all 0.2s;
}

.nav-item:hover {
  background: rgba(255,255,255,0.1);
  color: #fff;
}

.nav-item.active {
  background: rgba(255,255,255,0.15);
  color: #fff;
  border-left: 3px solid #00a854;
}

.nav-icon {
  font-size: 16px;
}

/* 主内容区 */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 顶部栏 */
.top-bar {
  height: 60px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  flex-shrink: 0;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.avatar {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #00a854, #52c41a);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 600;
  font-size: 14px;
}

.username {
  color: #666;
}

/* 内容区 */
.content-area {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

/* 操作卡片区 */
.action-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

/* 卡片 */
.card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
}

.card-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

/* 表单 */
.form {
  padding: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.required {
  color: #f5222d;
}

.optional {
  color: #999;
  font-weight: normal;
  font-size: 12px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 20px 0 12px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

input, textarea, select {
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  transition: border-color 0.2s;
}

input:focus, textarea:focus, select:focus {
  border-color: #00a854;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0,168,84,0.1);
}

input::placeholder, textarea::placeholder {
  color: #bfbfbf;
}

/* 表单按钮 */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.btn-primary {
  height: 36px;
  padding: 0 20px;
  background: #00a854;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-primary:hover {
  background: #00994d;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  height: 36px;
  padding: 0 20px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  border-color: #00a854;
  color: #00a854;
}

.error-msg {
  color: #f5222d;
  font-size: 13px;
  margin: 12px 0 0;
}

/* 表格卡片 */
.table-card {
  margin-top: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.search-input {
  height: 32px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 13px;
  min-width: 240px;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #00a854;
}

.btn-search {
  height: 32px;
  padding: 0 16px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.btn-search:hover {
  border-color: #00a854;
  color: #00a854;
}

.btn-icon {
  width: 32px;
  height: 32px;
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.2s;
}

.btn-icon:hover {
  background: #e8e8e8;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 表格 */
.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
}

.data-table th {
  background: #fafafa;
  font-weight: 600;
  color: #333;
  font-size: 13px;
}

.data-table tbody tr:hover {
  background: #f9f9f9;
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.empty-row {
  text-align: center !important;
  color: #999;
  padding: 40px !important;
}

.link-cell {
  color: #00a854;
}

.action-cell {
  display: flex;
  gap: 8px;
}

/* 小按钮 */
.btn-sm {
  height: 28px;
  padding: 0 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-edit {
  background: #e6f7ff;
  color: #1890ff;
}

.btn-edit:hover {
  background: #bae7ff;
}

.btn-node {
  background: #f6ffed;
  color: #52c41a;
}

.btn-node:hover {
  background: #d9f7be;
}

.btn-delete {
  background: #fff1f0;
  color: #f5222d;
}

.btn-delete:hover {
  background: #ffccc7;
}

/* 响应式 */
@media (max-width: 1200px) {
  .action-cards {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .sidebar {
    display: none;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
}

/* 可点击行 */
.clickable-row {
  cursor: pointer;
}

/* 弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: #fff;
  border-radius: 8px;
  width: 560px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-large {
  width: 720px;
}

.modal-small {
  width: 420px;
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.modal-close:hover {
  background: #f5f5f5;
  color: #333;
}

.modal-body {
  padding: 20px;
  max-height: calc(90vh - 120px);
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

/* Tabs */
.tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e8e8e8;
  margin-bottom: 20px;
}

.tab {
  padding: 12px 20px;
  border: none;
  background: none;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: all 0.2s;
}

.tab:hover {
  color: #00a854;
}

.tab.active {
  color: #00a854;
  border-bottom-color: #00a854;
  font-weight: 500;
}

.tab-content {
  min-height: 200px;
}

/* 添加节点区域 */
.add-record-section {
  background: #fafafa;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.add-record-section h4 {
  margin: 0 0 12px;
  font-size: 14px;
  color: #333;
}

.inline-form {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.inline-form .form-group {
  min-width: 120px;
}

.inline-form .form-group.flex-grow {
  flex: 1;
  min-width: 200px;
}

.inline-form .form-group label {
  font-size: 12px;
  margin-bottom: 4px;
}

.inline-form input,
.inline-form select {
  height: 36px;
}

.inline-form .btn-primary {
  height: 36px;
  white-space: nowrap;
}

/* 节点列表 */
.records-list {
  max-height: 300px;
  overflow-y: auto;
}

.empty-records {
  text-align: center;
  color: #999;
  padding: 40px;
}

/* 时间线 */
.timeline {
  position: relative;
  padding-left: 24px;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 6px;
  top: 8px;
  bottom: 8px;
  width: 2px;
  background: #e8e8e8;
}

.timeline-item {
  position: relative;
  padding-bottom: 16px;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-dot {
  position: absolute;
  left: -24px;
  top: 4px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #d9d9d9;
}

.timeline-dot.status-inbound { border-color: #1890ff; background: #e6f7ff; }
.timeline-dot.status-outbound { border-color: #722ed1; background: #f9f0ff; }
.timeline-dot.status-origin { border-color: #fa8c16; background: #fff7e6; }
.timeline-dot.status-destination { border-color: #13c2c2; background: #e6fffb; }
.timeline-dot.status-delivery { border-color: #52c41a; background: #f6ffed; }
.timeline-dot.status-signed { border-color: #00a854; background: #00a854; }

.timeline-content {
  padding-left: 8px;
}

.timeline-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}

.status-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-tag.status-inbound { background: #e6f7ff; color: #1890ff; }
.status-tag.status-outbound { background: #f9f0ff; color: #722ed1; }
.status-tag.status-origin { background: #fff7e6; color: #fa8c16; }
.status-tag.status-destination { background: #e6fffb; color: #13c2c2; }
.status-tag.status-delivery { background: #f6ffed; color: #52c41a; }
.status-tag.status-signed { background: #f6ffed; color: #00a854; }

.timeline-time {
  font-size: 13px;
  color: #666;
}

.timeline-desc {
  font-size: 13px;
  color: #333;
  margin-top: 4px;
}

/* 分页 */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  border-top: 1px solid #f0f0f0;
}

.page-btn {
  height: 32px;
  padding: 0 16px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  border-color: #00a854;
  color: #00a854;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  color: #666;
  font-size: 13px;
}

/* 节点操作 */
.record-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.btn-xs {
  height: 22px;
  padding: 0 8px;
  border: none;
  border-radius: 3px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-xs.btn-edit {
  background: #e6f7ff;
  color: #1890ff;
}

.btn-xs.btn-delete {
  background: #fff1f0;
  color: #f5222d;
}

.btn-xs.btn-save {
  background: #f6ffed;
  color: #52c41a;
}

.btn-xs.btn-cancel {
  background: #f5f5f5;
  color: #666;
}

.record-edit-form {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
}

.record-edit-form input {
  height: 28px;
  padding: 0 8px;
  font-size: 12px;
}

.record-edit-form input:first-child {
  flex: 1;
}
</style>

